import React from 'react';
import { Alert, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { DatabaseCosts as DatabaseCostsData } from '../../types';
import { formatDate } from '../../utils/format';
import { count, megabytes, money } from '../../utils/costs';
import { CostPanel, Figure, Figures } from './CostPanel';

/** The database: how full it is, what fills it, and the provider's usage for the billing period. */
const DatabaseCosts: React.FC<{ database: DatabaseCostsData }> = ({ database }) => {
  const neon = database.neon;
  const planLimit = neon && neon.available ? neon.logical_limit_mb : null;
  const limit = planLimit ?? database.ingest_limit_mb;
  const used = database.size_mb !== null && limit ? Math.min(100, Math.round((database.size_mb / limit) * 100)) : null;
  return (
    <>
      <CostPanel title="Size" description="The whole database, measured by the database itself.">
        <Figures label="Database size">
          <Figure label="Data stored" value={megabytes(database.size_mb)} />
          <Figure
            label={planLimit ? "The plan's limit" : 'Limit'}
            value={limit ? megabytes(limit) : 'None set'}
            hint={planLimit ? 'the provider refuses writes above this' : limit ? 'job loading stops here' : undefined}
          />
          <Figure
            label="Job loading stops at"
            value={database.ingest_limit_mb ? megabytes(database.ingest_limit_mb) : 'No limit'}
            hint={database.ingest_limit_mb ? 'a safeguard below the plan limit' : undefined}
          />
          <Figure label="Used" value={used === null ? 'Not known' : `${used}%`} />
        </Figures>
        {used !== null && (
          <LinearProgress
            variant="determinate"
            value={used}
            color={used >= 90 ? 'error' : used >= 75 ? 'warning' : 'primary'}
            aria-label="Share of the storage limit used"
            sx={{ mt: 2, height: 8, borderRadius: 1 }}
          />
        )}
        {database.size_mb === null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This database ({database.engine}) does not report its size.
          </Typography>
        )}
      </CostPanel>

      <CostPanel title="Provider usage" description="The current billing period as the database provider reports it.">
        {!neon || !neon.available ? (
          <Alert severity="info">
            {neon?.detail ?? 'Not available.'}
            {neon?.reason === 'not_configured' &&
              ' To see compute hours and an estimated bill here, paste a Neon API key as NEON_API_KEY in the Secrets Manager secret jobflo/prod/keys. It is picked up within five minutes; nothing needs deploying.'}
          </Alert>
        ) : (
          <Figures label="Provider usage this billing period">
            <Figure
              label="Estimated cost"
              value={money(neon.estimated_cost)}
              hint={`at ${money(neon.rates.compute_hour ?? null, 3)} per compute hour, ${money(neon.rates.gb_month ?? null)} per GB-month`}
            />
            <Figure label="Compute" value={`${neon.compute_hours.toLocaleString('en-US')} CU-hours`} hint={`awake ${neon.active_hours.toLocaleString('en-US')} hours`} />
            <Figure label="Storage" value={`${neon.storage_gb_months.toLocaleString('en-US')} GB-months`} />
            <Figure
              label="Billing period"
              value={neon.period_start ? formatDate(neon.period_start) : 'Not known'}
              hint={neon.period_end ? `to ${formatDate(neon.period_end)}` : undefined}
            />
          </Figures>
        )}
      </CostPanel>

      {database.tables.length > 0 && (
        <CostPanel title="Largest tables">
          <TableContainer>
            <Table size="small" aria-label="Largest tables">
              <TableHead>
                <TableRow>
                  <TableCell>Table</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Rows (about)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {database.tables.map((table) => (
                  <TableRow key={table.name}>
                    <TableCell>{table.name}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{megabytes(table.size_mb)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{count(table.rows)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CostPanel>
      )}
    </>
  );
};

export default DatabaseCosts;
